import { useState, useEffect } from 'react';
import axios from 'axios';

let cachedUser = null;
let cachedToken = null;
let authPromise = null;

/**
 * Hook de autenticação - Decodifica JWT e fornece dados do usuário
 * 
 * ✅ Seguro: Apenas token é armazenado em localStorage
 * ✅ Dados do usuário vêm do backend (/api/auth/me)
 * ✅ Se token for alterado, a assinatura invalida e backend rejeita
 * 
 * @returns {
 *   user: { id, username, cargo, condominios: [{id, nome}, ...] },
 *   loading: boolean,
 *   isAuthenticated: boolean,
 *   logout: function
 * }
 */
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggerReload, setTriggerReload] = useState(0);

  useEffect(() => {
    const handleAuthTokenChanged = () => {
      setTriggerReload((prev) => prev + 1);
    };

    window.addEventListener('auth-token-changed', handleAuthTokenChanged);
    return () => {
      window.removeEventListener('auth-token-changed', handleAuthTokenChanged);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const token = localStorage.getItem('token');
    const apiAuthUrl = process.env.REACT_APP_API_AUTH_URL || '';

    if (!token) {
      if (isMounted) {
        setLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    const carregarUsuario = async () => {
      if (cachedUser && cachedToken === token) {
        if (isMounted) {
          setUser(cachedUser);
          setLoading(false);
        }
        return;
      }

      if (authPromise && cachedToken === token) {
        try {
          const userData = await authPromise;
          if (isMounted) {
            setUser(userData);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
        return;
      }

      try {
        cachedToken = token;
        authPromise = axios
          .get(`${apiAuthUrl}/api/auth/me`)
          .then((response) => response?.data?.dados);

        const dados = await authPromise;
        authPromise = null;

        if (!dados) {
          localStorage.removeItem('token');
          if (isMounted) {
            setUser(null);
          }
          return;
        }

        const userData = {
          id: dados.id,
          username: dados.username,
          cargo: dados.cargo,
          condominios: dados.condominios || []
        };

        cachedUser = userData;
        if (isMounted) {
          setUser(userData);
        }
      } catch (error) {
        if (isMounted) {
          console.error('[AUTH] Erro ao carregar usuário:', error);
        }
        authPromise = null;
        if (error?.response?.status === 401) {
          localStorage.removeItem('token');
          cachedUser = null;
          cachedToken = null;
          if (isMounted) {
            setUser(null);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    carregarUsuario();
    return () => {
      isMounted = false;
    };
  }, [triggerReload]);

  const logout = () => {
    localStorage.removeItem('token');
    cachedUser = null;
    cachedToken = null;
    authPromise = null;
    setUser(null);
    window.dispatchEvent(new Event('auth-token-changed'));
  };

  return {
    user,
    loading,
    logout,
    isAuthenticated: !!user
  };
};

export default useAuth;
